import type { Dispatch, SetStateAction } from "react";
import React, { useCallback, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import NiceModal from "@ebay/nice-modal-react";
import {
  useTranslate,
  useProductConfiguratorSliderImageScroll,
  getImageUrlFromSessionStorage,
} from "hooks";
import s from "components/ProductConfigurator/ProductConfigurator.module.scss";
import { IconButton } from "components/IconButton/IconButton";
import { ProductImageModal } from "components/_Modals/ProductImageModal";
import { ProductImage } from "components/ProductImage";
import { useDeviceType } from "components/Layout/useDeviceType";
import type { ProductImages as ProductImagesType } from "stores/product";

export const ImageCarousel = ({
  selectedImageIndex,
  productName,
  images,
  setSelectedImageIndex,
  defaultImage,
  disabled,
}: {
  selectedImageIndex: number;
  productName: string;
  images: ProductImagesType;
  setSelectedImageIndex: Dispatch<SetStateAction<number>>;
  defaultImage?: Scrivito.Obj;
  disabled?: boolean;
}) => {
  const translate = useTranslate();
  const deviceType = useDeviceType();
  const [hoveredImageIndex, setHoveredImageIndex] = useState<number | null>(
    null,
  );
  const { scrollContainerRef, imageRefs, handleWheel } =
    useProductConfiguratorSliderImageScroll(selectedImageIndex);

  const handleImageClick = (index: number) => {
    if (index !== selectedImageIndex) {
      setSelectedImageIndex(index);
    }
  };

  const onWheel: React.WheelEventHandler<HTMLDivElement> = useCallback(
    (e) => handleWheel(e, images.length, setSelectedImageIndex),
    [handleWheel, images.length, setSelectedImageIndex],
  );

  const handleOpenImageInNewTab = useCallback(() => {
    const currentImage = images[selectedImageIndex];
    if (currentImage?.pimId) {
      const imageUrl = getImageUrlFromSessionStorage(currentImage.pimId);
      if (imageUrl) {
        window.open(imageUrl, "_blank");
      }
    }
  }, [images, selectedImageIndex]);

  return (
    <div
      ref={scrollContainerRef}
      className={s.layoutMediaImgContainer}
      onWheel={onWheel}
    >
      {images.map((image, index) => {
        const isActive = index === selectedImageIndex;
        const isHovered = index === hoveredImageIndex;

        return (
          <div
            key={index}
            ref={(el) => {
              imageRefs.current[index] = el;
            }}
            className={cx(
              s.layoutMediaImgContainerImgCard,
              "position-relative p-3 rounded-2 cursor-pointer",
              {
                [s.imageCardBorder]: isActive && !disabled,
                [s.imageCardBorderDisabled]: isActive && disabled,
                "shadow-sm": !isHovered && !disabled,
                [s.imageCardShadow]: isHovered && !disabled,
              },
            )}
            onClick={() => handleImageClick(index)}
            onMouseEnter={() => setHoveredImageIndex(index)}
            onMouseLeave={() => setHoveredImageIndex(null)}
          >
            <div className={cx(s.imageCard, "position-relative h-100 w-100")}>
              {images.length > 0
                ? image?.pimId && (
                    <>
                      <ProductImage
                        pimId={image?.pimId}
                        width={deviceType === "desktop" ? 210 : 86}
                        height={deviceType === "desktop" ? 166 : 86}
                        productName={productName}
                      />
                      <div className={s.imageCardImageLabel}>{image?.text}</div>
                    </>
                  )
                : defaultImage && (
                    <Scrivito.ImageTag
                      className={cx(
                        "img-fluid w-100 h-100",
                        {
                          "opacity-25": !isActive,
                        },
                        s.imageCardImage,
                      )}
                      content={defaultImage}
                    />
                  )}
            </div>

            {isActive && (
              <div className={s.imageCardIconsContainer}>
                <IconButton
                  testId="media-header-arrows-maximize-icon"
                  id="media-header-arrows-maximize-icon"
                  icon="arrows-maximize-regular"
                  color="tertiary"
                  iconClassName={cx("icon-14 p-0")}
                  className="border-0"
                  tooltipText={translate("MAXIMIZE")}
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    NiceModal.show(ProductImageModal, {
                      image: images[selectedImageIndex],
                      productName,
                    });
                  }}
                />
                <IconButton
                  testId="media-img-arrow-up-right-square-regular-icon"
                  id="media-img-arrow-up-right-square-regular-icon"
                  icon="arrow-up-right-square-regular"
                  color="tertiary"
                  iconClassName={cx("icon-14 p-0")}
                  className="border-0"
                  tooltipText={translate(
                    "PRODUCT_CONFIGURATOR_MEDIA_VIEW_IN_A_NEW_TAB",
                  )}
                  disabled={disabled}
                  // Todo: implement open image in a new tab
                  onClick={handleOpenImageInNewTab}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
